import { Container, Graphics, IPoint, Point, Sprite, WRAP_MODES } from 'pixi.js';

import { Shared } from '../../App';
import { TrailDisplay } from '../../display/TrailDisplay';
import { Maths } from '../../utils/Maths';
import { BaseEntity } from './BaseEntity';
import { WheelEntity } from './WheelEntity';

export interface TrailData
{
    name: string,
    type: string,
    image: string,
    blend: string,
    speed: number,
    steps: number,
    ghost: boolean,
    coinPrice?: number,
    diamondPrice?: number,
    unlocked?: boolean;
}

export class TrailEntity extends BaseEntity<TrailData>
{
    public trail?: TrailDisplay;
    public wheel: WheelEntity;
    public prevX = 0;
    public prevY = 0;
    public step = 3;
    public px = 0;
    public py = 0;
    public history: IPoint[];

    constructor(wheel: WheelEntity)
    {
        super();
        this.wheel = wheel;
        this.wheel.physics.physicsFreeUpdateRunner.add(this as any);
        this.history = [];
    }

    public build(data: TrailData): void
    {
        this.data = data;
        this.data.steps = 50;

        const texture = Shared.assets.getTexture(this.data.image);

        texture.baseTexture.wrapMode = WRAP_MODES.REPEAT;

        if (!this.trail)
        {
            const points = [] as Point[];
            const alphas = [] as number[];

            for (let i = 0; i < this.data.steps; i++)
            {
                points.push(new Point());
                alphas.push(1 - Math.min(1, 2 / this.data.steps * i));
            }

            alphas[0] = 0;
            alphas[1] = 0.75;
            this.trail = new TrailDisplay(texture, points, alphas);
        }
        else
        {
            this.trail.material.texture = texture;
        }

        this.history = this.trail.points.slice(0);
        this.view.addChild(this.trail);
        this.px = this.wheel.physics.x;
        this.py = this.wheel.physics.y;
        this.trail.thickness = this.wheel.viewHeight * 0.8;
        this.trail.width = this.trail.thickness / this.trail.texture.height * 256;
        this.trail.alpha = 0;
        this.trail.renderable = false;
        this.snap();
    }

    public cleanup(): void
    {
        if (!this.trail) return;
        if (this.trail.parent) this.trail.parent.removeChild(this.trail);
        this.trail.destroy();
        this.trail = undefined;
    }

    public snap(): void
    {
        if (!this.trail || !this.wheel.physics) return;
        for (const point of this.history)
        {
            point.x = this.wheel.physics.x;
            point.y = this.wheel.physics.y;
        }
        this.px = this.wheel.physics.x;
        this.py = this.wheel.physics.y;
        this.trail.updateTransform();
        this.trail.updateTrail();
    }

    public physicsFreeUpdate(): void
    {
        if (!this.trail || !this.data) return;

        const vx = this.wheel.physics.x - this.px;
        const vy = this.wheel.physics.y - this.py;
        const speed = Math.abs(vx) + Math.abs(vy);

        if (speed > 70)
        {
            this.snap();

            return;
        }

        const alpha = Maths.clamp(speed * 0.5, 0, 1);
        const scale = vx >= 0 ? 1 : -1;
        const first = this.trail.points.pop() as Point;

        first.x = this.wheel.physics.x;
        first.y = this.wheel.physics.y;

        this.trail.points.unshift(first);
        this.trail.alpha = Maths.lerp(this.trail.alpha, alpha, 0.5) * this.wheel.view.alpha;

        if (this.data.speed)
        {
            this.trail.offset -= (speed * 0.002) - this.data.speed;
        }
        else if (this.trail.offset)
        {
            this.trail.offset = 0;
        }

        this.trail.thickness = this.wheel.viewHeight * 0.8 * scale;

        this.px = this.wheel.physics.x;
        this.py = this.wheel.physics.y;
        this.trail.renderable = this.trail.alpha > 0.1;
    }

    public static mountIcon(data: string | TrailData, size = 144, container?: Container): Container
    {
        if (!container) container = new Container();

        if (typeof data === 'string') data = Shared.assets.getData<TrailData>(`trail-${data}`);

        const texture = Shared.assets.getTexture(data.image);

        texture.baseTexture.wrapMode = WRAP_MODES.REPEAT;

        const sprite = Sprite.from(texture);

        container.addChild(sprite);
        sprite.anchor.set(0.5);

        const mask = new Graphics();

        mask.beginFill(0xFF0000, 0.5);
        // mask.drawCircle(0, 0, 62);
        mask.drawRect(-size / 2, -size / 2, size, size);
        container.addChild(mask);
        sprite.mask = mask;

        return container;
    }
}
